import { normalizeTimeJailValue } from '../utils/time-jail.js';
import { getHighestCharacterLevel } from '../utils/levels.js';
import { normalizeDiscordId } from '../utils/discord.js';
import { getDateSortValue } from '../utils/dates.js';

const IDENTITY_KEY = 'brb_identity';
const storedIdentity = (() => {
  try {
    const parsed = JSON.parse(localStorage.getItem(IDENTITY_KEY) || 'null');
    if (!parsed || !parsed.name) return null;
    return { playerId: parsed.playerId ?? null, name: String(parsed.name), isGuest: !!parsed.isGuest };
  } catch {
    return null;
  }
})();

const normalizeIdentityKey = (value) => String(value || '').trim().toLowerCase();

const VIEW_MODE_KEY = 'brb_view_mode_by_player';
const storedViewModes = (() => {
  try {
    const parsed = JSON.parse(localStorage.getItem(VIEW_MODE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
})();

const getViewModeKey = (identity) => {
  if (!identity) return null;
  if (identity.playerId) return `id:${identity.playerId}`;
  if (identity.name) return `name:${normalizeIdentityKey(identity.name)}`;
  return null;
};

const persistViewModes = () => {
  try {
    localStorage.setItem(VIEW_MODE_KEY, JSON.stringify(storedViewModes));
  } catch {}
};

const sanitizeCharacter = (character) => ({
  ...character,
  time_jail: normalizeTimeJailValue(character.time_jail),
  hp: Number(character.hp) || 0
});

const normalizePlayerKey = (value) => String(value || '').trim().toLowerCase();

const sanitizePlayer = (player) => ({
  ...player,
  name: String(player?.name || '').trim(),
  discord_id: normalizeDiscordId(player?.discord_id)
});

const sortPlayers = (players = []) =>
  players
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

const sanitizePatch = (patch) => {
  if (!patch) return patch;
  const out = { ...patch };
  if (Object.prototype.hasOwnProperty.call(patch, 'time_jail')) out.time_jail = normalizeTimeJailValue(patch.time_jail);
  if (Object.prototype.hasOwnProperty.call(patch, 'hp')) out.hp = Number(patch.hp) || 0;
  return out;
};

const state = {
  characters: [],
  players: [],
  locations: [],
  sessions: [],
  neverForgetEvents: [],
  levelUpEvents: [],
  filters: {
    search: '',
    status: '',
    class: '',
    location: '',
    player: ''
  },
  identity: storedIdentity,
  viewMode: storedViewModes[getViewModeKey(storedIdentity)] === 'dm' ? 'dm' : 'player',
  activeTab: 'roster',
  sort: { key: 'name', direction: 'asc' },
  memoriamSort: { key: 'deathday', direction: 'desc' },
  modal: {
    characterId: null,
    edit: false
  },
  party: {
    location: '',
    members: new Set()
  },
  loading: false,
  error: null
};

const listeners = new Set();
const notify = () => listeners.forEach((cb) => cb(getState()));

const reconcileCharacterState = () => {
  const availableCharacters = state.characters;
  const availableSlugs = new Set(
    availableCharacters
      .map((character) => String(character.slug || '').toLowerCase())
      .filter(Boolean)
  );
  const availablePlayers = new Set(availableCharacters.map((character) => character.player).filter(Boolean));
  const availableClasses = new Set(
    availableCharacters.flatMap((character) =>
      String(character.class || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );
  const availableLocations = new Set(
    availableCharacters.map((character) => character.current_location).filter(Boolean)
  );
  const availableStatuses = new Set(availableCharacters.map((character) => character.status).filter(Boolean));

  state.party.members.forEach((slug) => {
    if (availableSlugs.has(slug)) return;
    state.party.members.delete(slug);
  });

  if (state.filters.player && !availablePlayers.has(state.filters.player)) {
    state.filters.player = '';
  }

  if (state.filters.class && !availableClasses.has(state.filters.class)) {
    state.filters.class = '';
  }

  if (state.filters.location && !availableLocations.has(state.filters.location)) {
    state.filters.location = '';
  }

  if (state.filters.status && !availableStatuses.has(state.filters.status)) {
    state.filters.status = availableStatuses.has('Alive') ? 'Alive' : '';
  }

  if (state.party.location && !availableLocations.has(state.party.location)) {
    state.party.location = Array.from(availableLocations).sort()[0] || '';
    state.party.members.clear();
  }

  if (
    state.modal.characterId !== null &&
    !availableCharacters.some((character) => character.id === state.modal.characterId)
  ) {
    state.modal = { characterId: null, edit: false };
  }
};

const removeCharacters = (predicate) => {
  const removed = state.characters.filter(predicate);
  if (!removed.length) return;

  const removedIds = new Set(removed.map((character) => character.id));

  state.characters = state.characters.filter((character) => !removedIds.has(character.id));
  reconcileCharacterState();
  notify();
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getState = () => ({
  ...state,
  party: { ...state.party, members: new Set(state.party.members) }
});

export const setLoading = (value) => {
  state.loading = value;
  notify();
};

export const setCharacters = (list) => {
  state.characters = list.map(sanitizeCharacter);
  reconcileCharacterState();
  notify();
};

export const setPlayers = (list) => {
  state.players = sortPlayers((Array.isArray(list) ? list : []).map(sanitizePlayer));
  notify();
};

export const setLocations = (list) => {
  state.locations = Array.isArray(list) ? list : [];
  reconcileCharacterState();
  notify();
};

export const addLocationLocal = (location) => {
  if (!location) return;
  state.locations = [...state.locations, location];
  reconcileCharacterState();
  notify();
};

const normalizeLocationKey = (value) => String(value || '').trim().toLowerCase();

export const updateLocationLocal = (id, patch = {}) => {
  const existing = state.locations.find((location) => location.id === id);
  if (!existing) return;

  const oldKey = normalizeLocationKey(existing.name);
  const updated = { ...existing, ...patch };
  state.locations = state.locations.map((location) => (location.id === id ? updated : location));

  if (oldKey && normalizeLocationKey(updated.name) !== oldKey) {
    state.characters = state.characters.map((character) => {
      const matchesCurrent = normalizeLocationKey(character.current_location) === oldKey;
      const matchesBirth = normalizeLocationKey(character.birth_location) === oldKey;
      if (!matchesCurrent && !matchesBirth) return character;
      return sanitizeCharacter({
        ...character,
        current_location: matchesCurrent ? updated.name : character.current_location,
        birth_location: matchesBirth ? updated.name : character.birth_location
      });
    });
  }

  reconcileCharacterState();
  notify();
};

export const setSessions = (list) => {
  state.sessions = Array.isArray(list) ? list : [];
  notify();
};

export const addSessionLocal = (session) => {
  if (!session) return;
  state.sessions = [session, ...state.sessions];
  notify();
};

export const updateSessionLocal = (id, patch) => {
  state.sessions = state.sessions.map((session) => (session.id === id ? { ...session, ...patch } : session));
  notify();
};

export const removeSessionLocal = (id) => {
  state.sessions = state.sessions.filter((session) => session.id !== id);
  notify();
};

export const setNeverForgetEvents = (list) => {
  state.neverForgetEvents = Array.isArray(list) ? list : [];
  notify();
};

export const addNeverForgetEventLocal = (event) => {
  if (!event) return;
  state.neverForgetEvents = [event, ...state.neverForgetEvents];
  notify();
};

export const updateNeverForgetEventLocal = (id, patch) => {
  state.neverForgetEvents = state.neverForgetEvents.map((evt) =>
    evt.id === id ? { ...evt, ...patch } : evt
  );
  notify();
};

export const setLevelUpEvents = (list) => {
  state.levelUpEvents = Array.isArray(list) ? list : [];
  notify();
};

export const addLevelUpEventLocal = (event) => {
  if (!event) return;
  state.levelUpEvents = [event, ...state.levelUpEvents];
  notify();
};

export const setFilters = (patch) => {
  state.filters = { ...state.filters, ...patch };
  notify();
};

export const clearFilters = () => {
  state.filters = { search: '', status: '', class: '', location: '', player: '' };
  notify();
};

export const setSort = (key) => {
  if (!key) return;
  const { sort } = state;
  let direction = 'asc';
  if (sort.key === key) direction = sort.direction === 'asc' ? 'desc' : 'asc';
  state.sort = { key, direction };
  notify();
};

const MEMORIAM_SORT_DEFAULT_DIRECTION = { deathday: 'desc' };

export const setMemoriamSort = (key) => {
  if (!key) return;
  const { memoriamSort } = state;
  let direction = MEMORIAM_SORT_DEFAULT_DIRECTION[key] || 'asc';
  if (memoriamSort.key === key) direction = memoriamSort.direction === 'asc' ? 'desc' : 'asc';
  state.memoriamSort = { key, direction };
  notify();
};

export const openModal = (id) => {
  state.modal = { characterId: id, edit: false };
  notify();
};

export const openModalForEdit = (id) => {
  state.modal = { characterId: id, edit: true };
  notify();
};

export const closeModal = () => {
  state.modal = { characterId: null, edit: false };
  notify();
};

export const setModalEdit = (edit) => {
  state.modal = { ...state.modal, edit };
  notify();
};

export const updateCharacterLocal = (id, patch = {}) => {
  const safePatch = sanitizePatch(patch);
  state.characters = state.characters.map((c) => (c.id === id ? sanitizeCharacter({ ...c, ...safePatch }) : c));
  reconcileCharacterState();
  notify();
};

export const addCharacterLocal = (character) => {
  state.characters = [sanitizeCharacter(character), ...state.characters];
  reconcileCharacterState();
  notify();
};

export const addPlayerLocal = (player) => {
  const safePlayer = sanitizePlayer(player);
  if (!safePlayer.name) return;

  const playerKey = normalizePlayerKey(safePlayer.name);
  state.players = sortPlayers([
    ...state.players.filter((existingPlayer) => normalizePlayerKey(existingPlayer.name) !== playerKey),
    safePlayer
  ]);
  state.characters = state.characters.map((character) => {
    const characterPlayerKey = normalizePlayerKey(character.player);
    if (!characterPlayerKey || characterPlayerKey !== playerKey) return character;
    return sanitizeCharacter({
      ...character,
      player: safePlayer.name,
      discord_id: safePlayer.discord_id
    });
  });
  reconcileCharacterState();
  notify();
};

export const updatePlayerLocal = (id, patch = {}) => {
  const existing = state.players.find((player) => player.id === id);
  if (!existing) return;

  const oldKey = normalizePlayerKey(existing.name);
  const updated = sanitizePlayer({ ...existing, ...patch });
  state.players = sortPlayers(state.players.map((player) => (player.id === id ? updated : player)));
  state.characters = state.characters.map((character) => {
    if (normalizePlayerKey(character.player) !== oldKey) return character;
    return sanitizeCharacter({ ...character, player: updated.name, discord_id: updated.discord_id });
  });

  if (state.identity && state.identity.playerId === id) {
    state.identity = { ...state.identity, name: updated.name };
    try {
      localStorage.setItem(IDENTITY_KEY, JSON.stringify(state.identity));
    } catch {}
  }

  reconcileCharacterState();
  notify();
};

export const removeCharacterLocal = (id) => {
  removeCharacters((character) => character.id === id);
};

export const removeCharactersByPlayerLocal = (player) => {
  if (!player) return;
  removeCharacters((character) => character.player === player);
};

export const setPartyLocation = (location) => {
  state.party.location = location;
  state.party.members.clear();
  notify();
};

export const togglePartyMember = (slug) => {
  if (state.party.members.has(slug)) state.party.members.delete(slug);
  else state.party.members.add(slug);
  notify();
};

export const addPartyMembers = (slugs = []) => {
  let changed = false;
  slugs.forEach((slug) => {
    if (!slug) return;
    if (!state.party.members.has(slug)) {
      state.party.members.add(slug);
      changed = true;
    }
  });
  if (changed) notify();
};

export const clearParty = () => {
  state.party.members.clear();
  notify();
};

export const getCharacterById = (id) => state.characters.find((c) => c.id === id) || null;
export const getLocationByName = (name) => {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return null;
  return state.locations.find((location) => String(location.name || '').trim().toLowerCase() === normalized) || null;
};
export const setIdentity = (identity) => {
  if (!identity || !identity.name) return;
  state.identity = { playerId: identity.playerId ?? null, name: String(identity.name), isGuest: false };
  try {
    localStorage.setItem(IDENTITY_KEY, JSON.stringify(state.identity));
  } catch {}
  state.viewMode = storedViewModes[getViewModeKey(state.identity)] === 'dm' ? 'dm' : 'player';
  if (state.viewMode !== 'dm' && state.activeTab === 'locations') state.activeTab = 'roster';
  notify();
};

export const setGuestIdentity = () => {
  state.identity = { playerId: null, name: 'Guest', isGuest: true };
  try {
    localStorage.setItem(IDENTITY_KEY, JSON.stringify(state.identity));
  } catch {}
  state.viewMode = 'player';
  if (state.activeTab === 'locations') state.activeTab = 'roster';
  notify();
};

export const clearIdentity = () => {
  state.identity = null;
  state.viewMode = 'player';
  if (state.activeTab === 'locations') state.activeTab = 'roster';
  try {
    localStorage.removeItem(IDENTITY_KEY);
  } catch {}
  notify();
};

export const setViewMode = (mode) => {
  state.viewMode = mode === 'dm' ? 'dm' : 'player';
  if (state.viewMode !== 'dm' && state.activeTab === 'locations') state.activeTab = 'roster';
  const key = getViewModeKey(state.identity);
  if (key) {
    storedViewModes[key] = state.viewMode;
    persistViewModes();
  }
  notify();
};

export const setActiveTab = (tab) => {
  state.activeTab = tab || 'roster';
  notify();
};

export const getFilteredCharacters = () => {
  const { search, status, class: characterClass, location, player } = state.filters;
  const query = search.trim().toLowerCase();
  const scopedToIdentity = state.viewMode !== 'dm' && state.identity && !state.identity.isGuest;
  const identityKey = scopedToIdentity ? normalizeIdentityKey(state.identity.name) : null;
  const filtered = state.characters.filter((c) => {
    if (scopedToIdentity && normalizeIdentityKey(c.player) !== identityKey) return false;
    if (status && c.status !== status) return false;
    if (
      characterClass &&
      !String(c.class || '')
        .split(',')
        .map((value) => value.trim())
        .includes(characterClass)
    ) return false;
    if (location && c.current_location !== location) return false;
    if (player && c.player !== player) return false;
    if (query) {
      const haystack = `${c.name} ${c.player} ${c.class}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
  const sorter = (a, b) => {
    const { key, direction } = state.sort;
    const dir = direction === 'asc' ? 1 : -1;
    const getValue = (character) => {
      switch (key) {
        case 'player':
          return (character.player || '').toLowerCase();
        case 'status':
          return (character.status || '').toLowerCase();
        case 'class':
          return (character.class || '').toLowerCase();
        case 'level':
          return getHighestCharacterLevel(character.level, 0);
        case 'location':
          return (character.current_location || '').toLowerCase();
        case 'time_jail':
          return parseDateValue(character.time_jail);
        case 'name':
        default:
          return (character.name || '').toLowerCase();
      }
    };
    const va = getValue(a);
    const vb = getValue(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  };
  return filtered.slice().sort(sorter);
};
const parseDateValue = (value) => {
  if (!value) return 0;
  return getDateSortValue(value);
};
